import { useCallback, useRef, useState } from "react";
import ResetTvIcon from "./icons/reset-tv.svg?react";

export interface ControlOverlayProps {
  isPoweredOn: boolean;
  onPowerToggle: () => void;
  onVolumeUp: () => void;
  onVolumeDown: () => void;
  onChannelUp: () => void;
  onChannelDown: () => void;
  /** Called when return/reset button is pressed */
  onReturn?: () => void;
  /** Whether to show the return button */
  showReturnButton?: boolean;
}

interface ControlButtonProps {
  label: string;
  icon?: React.ReactNode;
  onPress: () => void;
  variant?: "default" | "power" | "accent" | "return";
}

function ControlButton({
  label,
  icon,
  onPress,
  variant = "default",
}: ControlButtonProps) {
  const [isPressed, setIsPressed] = useState(false);
  const touchedRef = useRef(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault(); // Prevent synthetic mouse events
    touchedRef.current = true;
    setIsPressed(true);
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault(); // Prevent synthetic mouse events
      setIsPressed(false);
      onPress();
      // Reset touch flag after a short delay to allow for edge cases
      setTimeout(() => {
        touchedRef.current = false;
      }, 100);
    },
    [onPress],
  );

  const handleMouseDown = useCallback(() => {
    // Ignore if this was triggered by touch
    if (touchedRef.current) return;
    setIsPressed(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    // Ignore if this was triggered by touch
    if (touchedRef.current) return;
    setIsPressed(false);
    onPress();
  }, [onPress]);

  const baseStyles: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 48,
    height: 48,
    borderRadius: 12,
    border: "none",
    cursor: "pointer",
    transition: "all 0.1s ease",
    fontFamily: "'Press Start 2P', monospace",
    fontSize: 11,
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: 1,
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
    userSelect: "none",
  };

  const variantStyles: Record<string, React.CSSProperties> = {
    default: {
      backgroundColor: isPressed
        ? "rgba(60, 60, 60, 0.95)"
        : "rgba(40, 40, 40, 0.9)",
      color: isPressed ? "#fff" : "rgba(255, 255, 255, 0.8)",
      boxShadow: isPressed
        ? "inset 0 2px 4px rgba(0, 0, 0, 0.4)"
        : "0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
      transform: isPressed ? "scale(0.95)" : "scale(1)",
    },
    power: {
      backgroundColor: isPressed
        ? "rgba(200, 60, 60, 0.95)"
        : "rgba(160, 40, 40, 0.9)",
      color: "#fff",
      boxShadow: isPressed
        ? "inset 0 2px 4px rgba(0, 0, 0, 0.4)"
        : "0 2px 8px rgba(0, 0, 0, 0.3), 0 0 12px rgba(255, 60, 60, 0.3)",
      transform: isPressed ? "scale(0.95)" : "scale(1)",
    },
    accent: {
      backgroundColor: isPressed
        ? "rgba(60, 120, 200, 0.95)"
        : "rgba(40, 90, 160, 0.9)",
      color: "#fff",
      boxShadow: isPressed
        ? "inset 0 2px 4px rgba(0, 0, 0, 0.4)"
        : "0 2px 8px rgba(0, 0, 0, 0.3), 0 0 12px rgba(60, 120, 255, 0.3)",
      transform: isPressed ? "scale(0.95)" : "scale(1)",
    },
    return: {
      backgroundColor: isPressed
        ? "rgba(100, 120, 255, 0.4)"
        : "rgba(100, 120, 255, 0.15)",
      color: "#fff",
      border: "1px solid rgba(100, 120, 255, 0.5)",
      boxShadow: isPressed
        ? "inset 0 2px 4px rgba(0, 0, 0, 0.3)"
        : "0 4px 20px rgba(0, 0, 0, 0.3)",
      transform: isPressed ? "scale(0.95)" : "scale(1)",
    },
  };

  return (
    <button
      type="button"
      style={{ ...baseStyles, ...variantStyles[variant] }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={(e) => {
        e.preventDefault();
        setIsPressed(false);
      }}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={() => {
        if (!touchedRef.current) setIsPressed(false);
      }}
      aria-label={label}
    >
      {icon || label}
    </button>
  );
}

/**
 * Mobile-only control overlay for operating the CRT TV
 * Positioned at the bottom of the screen for easy thumb access
 */
export function ControlOverlay({
  onPowerToggle,
  onVolumeUp,
  onVolumeDown,
  onChannelUp,
  onChannelDown,
  onReturn,
  showReturnButton = false,
}: ControlOverlayProps) {
  return (
    <div
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        display: "flex",
        justifyContent: "center",
        padding: "16px 12px 24px",
        background:
          "linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.4) 70%, transparent 100%)",
        pointerEvents: "none",
        zIndex: 1000,
      }}
      className="control-overlay"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          pointerEvents: "auto",
        }}
      >
        {/* Power Button */}
        <ControlButton label="⏻" onPress={onPowerToggle} variant="power" />

        {/* Spacer */}
        <div style={{ width: 10 }} />

        {/* Volume Controls */}
        <div
          style={{ display: "flex", flexDirection: "column", gap: 6 }}
          aria-label="Volume controls"
        >
          <ControlButton label="+" onPress={onVolumeUp} />
          <ControlButton label="−" onPress={onVolumeDown} />
        </div>

        {/* Channel Controls */}
        <div
          style={{ display: "flex", flexDirection: "column", gap: 6 }}
          aria-label="Channel controls"
        >
          <ControlButton label="▲" onPress={onChannelUp} variant="accent" />
          <ControlButton label="▼" onPress={onChannelDown} variant="accent" />
        </div>

        {/* Return Button - only shown when camera has moved */}
        {showReturnButton && onReturn && (
          <>
            <div style={{ width: 8 }} />
            <ControlButton
              label="Return"
              icon={<ResetTvIcon style={{ width: 24, height: 24 }} />}
              onPress={onReturn}
              variant="return"
            />
          </>
        )}
      </div>

      {/* CSS to hide on desktop */}
      <style>
        {`
          .control-overlay {
            display: none !important;
          }
          
          @media (max-width: 768px), (hover: none) and (pointer: coarse) {
            .control-overlay {
              display: flex !important;
            }
          }
        `}
      </style>
    </div>
  );
}
